import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkIcon } from '@heroicons/react/24/outline';
import type { CollaborationJournal } from '../../types';
import type { RunLink, Turn, TurnStep } from '../../types/runExplorer';
import { childLinkOf, entryStartMs } from '../../utils/runTurnModel';
import { formatClock, formatMs } from './runExplorerShared';
import { runExplorerPath } from './runExplorerLinks';

export interface RunSelection {
  stepId: string;
  /** A tool call inside the step, when the tool call itself is selected. */
  entryId?: string;
}

/** Bar colour by kind; amber when the step ran with a problem, red when it failed. */
const KIND_COLOURS: Record<string, string> = {
  agent: 'bg-indigo-500',
  function: 'bg-purple-500',
  branch: 'bg-orange-500',
  entry: 'bg-teal-500',
  model: 'bg-blue-500',
  tool: 'bg-violet-500',
  child: 'bg-gray-500',
};

const time = (iso: string | null | undefined): number => (iso ? Date.parse(iso) : NaN);

interface Bar {
  key: string;
  label: string;
  depth: 0 | 1;
  start: number;
  end: number;
  colour: string;
  select: RunSelection;
  childLink?: RunLink;
  testId: string;
}

function barsForStep(step: TurnStep, children: RunLink[]): Bar[] {
  const start = time(step.startedAt);
  const end = time(step.endedAt);
  const colour =
    step.state === 'failed'
      ? 'bg-red-500'
      : step.state === 'ran-with-problem'
        ? 'bg-amber-500'
        : KIND_COLOURS[step.nodeType] ?? KIND_COLOURS.agent;
  const bars: Bar[] = [
    {
      key: step.stepId,
      label: step.stepId,
      depth: 0,
      start,
      end,
      colour,
      select: { stepId: step.stepId },
      testId: `timeline-step-${step.stepId}`,
    },
  ];
  const callBar = (entry: CollaborationJournal, label: string, kind: 'model' | 'tool'): Bar => ({
    key: entry.id,
    label,
    depth: 1,
    start: entryStartMs(entry),
    end: time(entry.createdAt),
    colour: kind === 'tool' && entry.error ? 'bg-red-500' : KIND_COLOURS[kind],
    select: kind === 'tool' ? { stepId: step.stepId, entryId: entry.id } : { stepId: step.stepId },
    testId: `timeline-${kind}-${entry.id}`,
  });
  for (const call of step.modelCalls) {
    const roundTrips = Array.isArray(call.metadata?.steps) ? (call.metadata!.steps as unknown[]).length : null;
    bars.push(
      callBar(
        call,
        `Model call · ${call.llmModel ?? 'model'}${roundTrips ? ` · ${roundTrips} ${roundTrips === 1 ? 'step' : 'steps'}` : ''}`,
        'model'
      )
    );
  }
  for (const call of step.toolCalls) {
    bars.push(callBar(call, `Tool · ${call.functionName ?? 'call'}`, 'tool'));
    const child = childLinkOf(call, children);
    if (child) {
      bars.push({
        key: `child-${child.runId}`,
        label: `Child run · ${child.collaborationName ?? child.runId.slice(0, 8)}`,
        depth: 1,
        start: time(child.startedAt) || entryStartMs(call),
        end: time(child.completedAt) || time(call.createdAt),
        colour: KIND_COLOURS.child,
        select: { stepId: step.stepId, entryId: call.id },
        childLink: child,
        testId: `timeline-child-${child.runId}`,
      });
    }
  }
  return bars;
}

function TurnRows({
  turn,
  span,
  children,
  selection,
  onSelect,
}: {
  turn: Turn;
  span: number;
  children: RunLink[];
  selection: RunSelection | null;
  onSelect: (selection: RunSelection) => void;
}) {
  const turnStart = time(turn.startedAt);
  const bars = turn.path.flatMap((id) => barsForStep(turn.steps[id], children));
  const pct = (ms: number) => (span > 0 ? Math.min(Math.max((ms / span) * 100, 0), 100) : 0);

  return (
    <div className="space-y-0.5">
      {bars.map((bar) => {
        const left = pct((Number.isFinite(bar.start) ? bar.start : turnStart) - turnStart);
        const width = Math.max(pct((Number.isFinite(bar.end) && Number.isFinite(bar.start) ? bar.end - bar.start : 0)), 0.4);
        const isSelected =
          selection?.stepId === bar.select.stepId && (selection?.entryId ?? null) === (bar.select.entryId ?? null);
        const duration = Number.isFinite(bar.end) && Number.isFinite(bar.start) ? bar.end - bar.start : null;
        return (
          <div key={bar.key} className="flex items-center gap-2 text-xs">
            <div
              className={`w-56 flex-shrink-0 truncate ${
                bar.depth === 1 ? 'pl-4 text-gray-500 dark:text-gray-400' : 'font-mono font-medium text-gray-800 dark:text-gray-100'
              }`}
              title={bar.label}
            >
              {bar.label}
              {bar.childLink && (
                <Link
                  to={runExplorerPath(bar.childLink.collaborationId, bar.childLink.runId)}
                  className="ml-1 inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:underline"
                  aria-label="Open child run"
                  data-testid={`timeline-child-link-${bar.childLink.runId}`}
                >
                  <LinkIcon className="h-3 w-3" />
                </Link>
              )}
            </div>
            <button
              type="button"
              data-testid={bar.testId}
              onClick={() => onSelect(bar.select)}
              className={`relative h-4 flex-1 rounded bg-gray-100 dark:bg-gray-800 ${isSelected ? 'ring-2 ring-indigo-400' : ''}`}
              aria-label={`${bar.label}, ${formatMs(duration)}`}
            >
              <span
                className={`absolute top-0.5 bottom-0.5 rounded ${bar.colour}`}
                style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }}
              />
            </button>
            <div className="w-16 flex-shrink-0 text-right text-gray-500 dark:text-gray-400">{formatMs(duration)}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Wall time per step (task 1151, step 8): one row per step in the turn on a
 * shared axis that starts at the turn's start, with the model calls and tool
 * calls nested under their step and a child run as its own row that links to
 * its page. "All turns" stacks the turns, each on the same scale.
 */
export function RunTimeline({
  turns,
  selectedTurnIndex,
  children,
  selection,
  onSelect,
}: {
  turns: Turn[];
  selectedTurnIndex: number;
  children: RunLink[];
  selection: RunSelection | null;
  onSelect: (selection: RunSelection) => void;
}) {
  const [allTurns, setAllTurns] = useState(false);
  const shown = allTurns ? turns : turns.filter((turn) => turn.index === selectedTurnIndex);
  const span = Math.max(1, ...shown.map((turn) => turn.durationMs ?? 0));
  const turn = turns.find((t) => t.index === selectedTurnIndex);

  return (
    <div data-testid="run-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
          Timeline{allTurns ? ', all turns' : turns.length > 1 ? `, turn ${selectedTurnIndex + 1}` : ''}
        </h2>
        {turns.length > 1 && (
          <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-xs" role="group" aria-label="Timeline scope">
            <button
              type="button"
              aria-pressed={!allTurns}
              onClick={() => setAllTurns(false)}
              className={`px-2.5 py-1 ${!allTurns ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
            >
              This turn
            </button>
            <button
              type="button"
              aria-pressed={allTurns}
              onClick={() => setAllTurns(true)}
              className={`px-2.5 py-1 ${allTurns ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
            >
              All turns
            </button>
          </div>
        )}
      </div>
      {!allTurns && turn && (
        <div className="flex justify-between text-[11px] text-gray-400 dark:text-gray-500 mb-1 ml-[14.5rem] mr-[4.5rem]">
          <span>{formatClock(turn.startedAt, true)}</span>
          <span>+{formatMs(span / 2)}</span>
          <span>{formatClock(turn.endedAt, true)}</span>
        </div>
      )}
      <div className="space-y-4">
        {shown.map((t) => (
          <div key={t.index}>
            {allTurns && (
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Turn {t.index + 1} · {formatClock(t.startedAt)} · {formatMs(t.durationMs)}
              </p>
            )}
            <TurnRows turn={t} span={span} children={children} selection={selection} onSelect={onSelect} />
          </div>
        ))}
        {shown.every((t) => t.path.length === 0) && (
          <p className="text-sm text-gray-500 dark:text-gray-400">No steps have run in this turn yet.</p>
        )}
      </div>
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        Bars are wall time. Blue is a model call, violet a tool call, purple a function, orange a branch, amber a step that ran with a problem.
      </p>
    </div>
  );
}
